import { forwardRef, useRef, useState, useContext, useEffect, useCallback } from 'react'
import { getModelValue } from '@aytm/app/stats/lib/stat-page'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'
import { useFillChartFrame } from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/chart-frame'
import {
  useIsRenderGated,
  useRenderGate
} from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/render-gate'
import {
  useExportableChart,
  svgToPngDataUrl,
  ExportContext
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'
import { useChartOptions } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useChartOptions'
import { useLegendVisibility } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useLegendVisibility'
import { useExport } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useExport'
import { useMarginOfError } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useMarginOfError'
import { useZoom } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useZoom'
import { useTooltipTeardown } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useTooltipTeardown'
import { resolveDrawableMode } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/chartRegistry'

import ChartModes from '@aytm/app/stats/page/question-views/components/charts/highcharts/components/chart-modes/ChartModes'
import Zoom from '@aytm/app/stats/page/question-views/components/charts/highcharts/components/zoom/Zoom'
import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'

import style from '@aytm/app/stats/page/question-views/components/charts/highcharts/styles/ChartContainer.module.scss'

const ChartContainer = forwardRef((props, ref) => {
  const {
    chartModeTypes = [],
    chartModeOptions,
    withDefault = true,
    chartMode,
    selectorChartModeValue,
    chartModeOverride,
    globalChartMode,
    onChartModeChange,
    viewOptionsDisabled,

    questionKey,
    // numeric question id → `chart_question_<id>` on the container so pptx.coffee's
    // `#chart_question_<id> svg` export selector resolves (was set coffee-side on @el)
    questionId,
    zoomType = false,
    zoomLevel,
    globalZoomLevel,
    onZoomChange,
    ignoreTraits,

    features,
    qType,
    qSubType,
    answerVariants,
    batches,

    // Composite parents (emd) serialize the HC svg together with their overlay
    // layers in their own useExportableChart — the generic attachment below must
    // not race it with a bare getSVG() snapshot.
    exportDelegated = false,

    // Panels that belong to the chart rather than beside it — a legend, an overlay —
    // render under it and inside the container's own box.
    children
  } = props

  // A parent may pass its own ref to reach the mounted chart instance (MaxDiff hands the
  // live axis extremes back to the PPTX display-range bridge). Local ref otherwise.
  const innerChartRef = useRef(null)
  const chartRef = props.chartRef || innerChartRef

  // what the placeholder above stood in for is this chart, so it is this chart that answers for it
  useFillChartFrame(chartRef)

  // A parent may own the measured box too -- it hands its ref in rather than wrapping this in a div.
  const { componentRef, width } = useComponentWidth({ ref, defaultWidth: 814 })

  // Headless export wiring. requestId is null on the live page → the hook below
  // no-ops and chartInstance is never set, so live rendering is untouched.
  const { requestId } = useContext(ExportContext)
  const [chartInstance, setChartInstance] = useState(null)

  // The report's percentage precision reaches every chart from here rather than from each of the
  // thirteen per-type props builders. The chart shells re-render on the setting, and the options
  // memo is keyed on a fresh object each render, so the value cannot go stale behind it.
  const reportPercentDecimals = props.reportPercentDecimals ?? getModelValue('percentDecimals')
  // MoE controller: created for every chart, but only wired by column/bar/dataLab builders. It takes
  // the precision too, so its legend states the interval as the tooltip beside it does.
  const moeController = useMarginOfError({ ...props, reportPercentDecimals })
  // the mode the chart is drawn in -- the one asked for, unless this question has no chart for it
  const drawnMode = resolveDrawableMode(chartMode, chartModeTypes)

  // An explicit width wins over the measured one — the info-view quadrant sizes itself.
  const chartResult = useChartOptions(drawnMode, {
    ...props,
    reportPercentDecimals,
    width: props.width || width,
    moeController
  })

  const { exportOptions, handleExport } = useExport({
    questionKey,
    chartRef,
    features,
    qType,
    qSubType,
    answerVariants,
    batches
  })

  // chartResult?.zoom is undefined for pie/non-zoom builders → isZoomEnabled: false
  const { isZoomEnabled, isMax, isWarning, isDisabled, title, getToggleTarget } = useZoom({
    zoom: chartResult?.zoom,
    zoomType,
    ignoreTraits,
    zoomLevel,
    globalZoomLevel
  })

  // Single attachment covering every HC chart-mode: rasterize the chart's own SVG.
  // A falsy dep keeps the hook permanently idle when the parent owns the export.
  // A missing builder is terminal, not a pending state — the registry lookup is
  // synchronous, so a null chartResult never resolves and would otherwise hang.
  useExportableChart({
    debugLabel: 'ChartContainer',
    deps: [!exportDelegated && (chartInstance || !chartResult)],
    prepareForExport: async () => {
      if (!chartResult) throw new Error(`No chart builder for chartMode '${drawnMode}'`)

      const svg = chartInstance.getSVG()
      const { chartWidth, chartHeight } = chartInstance
      return [await svgToPngDataUrl(svg, chartWidth, chartHeight)]
    }
  })

  useLegendVisibility({ chartRef, chartMode: drawnMode })

  const tooltipTeardown = useTooltipTeardown(chartRef)

  // a menu opened from the header must not open over the hint of the point it came from
  const dismissHint = useCallback(() => chartRef.current?.chart?.tooltip?.hide(0), [chartRef])

  // Drawn for an export: HC fires `load` once the axes, the series and any external images are in
  // the DOM, which is the earliest the svg is worth serializing. A chart with nothing to build has
  // no such moment, so it reports itself straight away.
  const chartRendered = useRenderGate()
  const isGated = useIsRenderGated()

  useEffect(() => {
    if (!chartResult) chartRendered()
  }, [chartResult, chartRendered])

  const showChartModes = chartModeTypes?.length >= 2 && !viewOptionsDisabled

  if (!chartResult) return null

  // Export-only config: animations off (so the snapshot isn't mid-transition) and a load handler —
  // it captures the instance for getSVG() headlessly, and reports the chart drawn to the gate that
  // is holding the export. Headless adds a white background (PPTX expects opaque) and drops the
  // native exporting menu. On the live page we pass the builder options through unchanged.
  const baseOptions = chartResult.options
  const headless = Boolean(requestId)
  const chartOptions =
    headless || isGated
      ? {
          ...baseOptions,
          chart: {
            ...baseOptions.chart,
            ...(headless ? { backgroundColor: '#ffffff' } : {}),
            animation: false,
            events: {
              ...baseOptions.chart?.events,
              load: function load() {
                baseOptions.chart?.events?.load?.call(this) // preserve builder's own load (e.g. sunburst/treemap)
                if (headless) setChartInstance(this)

                chartRendered()
              }
            }
          },
          plotOptions: {
            ...baseOptions.plotOptions,
            series: {
              ...baseOptions.plotOptions?.series,
              animation: false
            }
          },
          ...(headless ? { exporting: { ...baseOptions.exporting, enabled: false } } : {})
        }
      : baseOptions

  return (
    <div ref={componentRef} className={style.container}>
      <div
        className={style.header}
        onClickCapture={dismissHint}
        onKeyDownCapture={dismissHint}
        onMouseDownCapture={dismissHint}
      >
        <div className={style.leftControls}>
          {showChartModes && (
            <ChartModes
              chartModeTypes={chartModeTypes}
              chartModeOptions={chartModeOptions}
              chartMode={selectorChartModeValue}
              chartModeOverride={chartModeOverride}
              globalChartMode={globalChartMode}
              withDefault={withDefault}
              onChange={onChartModeChange}
            />
          )}
        </div>
        <div className={style.rightControls}>
          <ExportDropdown options={exportOptions} onExport={handleExport} />
        </div>
        {isZoomEnabled && !isDisabled && (
          <div className={style.zoom}>
            <Zoom
              isMax={isMax}
              isWarning={isWarning}
              title={title}
              onToggle={() => onZoomChange?.(questionKey, getToggleTarget())}
            />
          </div>
        )}
      </div>

      {/* key remounts the chart on mode switch: update() merges options and keeps
            leftovers from the previous builder (e.g. columnBar categories break the
            scatter axis) — full re-creation matches the legacy per-switch rebuild */}
      <div id={questionId == null ? undefined : `chart_question_${questionId}`} {...tooltipTeardown}>
        <HighchartsReact key={drawnMode} ref={chartRef} highcharts={Highcharts} options={chartOptions} />
      </div>

      {children}
    </div>
  )
})

ChartContainer.displayName = 'ChartContainer'

export default ChartContainer
